import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Timer } from "lucide-react"
import { PowerUpBar } from "@/components/game/power-up-bar"
import { StreakIndicator } from "@/components/game/streak-indicator"
import type { GameQuestion, PlayerPowerUp, GameState } from '@/types/games'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'

interface TriviaGameProps {
  gameId: string
  questions: GameQuestion[]
  powerUps?: PlayerPowerUp[]
  onComplete: (score: number, time: number) => void
  onUsePowerUp?: (powerUpId: string) => Promise<boolean>
}

export function TriviaGame({ 
  gameId,
  questions, 
  onComplete,
  powerUps: _powerUps,
  onUsePowerUp: _onUsePowerUp
}: TriviaGameProps) {
  const { user } = useAuth()
  const [gameState, setGameState] = useState<GameState>({
    currentRound: 0,
    score: 0,
    streak: 0,
    lives: 1,
    timeLeft: 30,
    powerUpsActive: [],
    hintsRemaining: 1
  })
  const [gameType, setGameType] = useState<'best_of_three' | 'total_score'>('total_score')

  useEffect(() => {
    // Load game type when component mounts
    loadGameType()

    if (gameState.timeLeft > 0) {
      const timer = setInterval(() => {
        setGameState(prev => ({
          ...prev,
          timeLeft: Math.max(0, prev.timeLeft - 1)
        }))
      }, 1000)
      return () => clearInterval(timer)
    } else {
      handleAnswer(-1) // Time's up, count as wrong answer
    }
  }, [gameState.timeLeft])

  async function loadGameType() {
    try {
      const { data } = await supabase
        .from('games')
        .select('type')
        .eq('id', gameId)
        .single()

      if (data) {
        setGameType(data.type)
      }
    } catch (error) {
      console.error('Error loading game type:', error)
    }
  }

  const handleAnswer = async (answerIndex: number) => {
    const question = questions[gameState.currentRound]
    const isCorrect = answerIndex === question.correct_answer
    const responseTime = 30 - gameState.timeLeft
    
    // Calculate score
    let points = question.base_score || 1000
    let multiplier = 1

    // Apply time bonus if answered correctly
    if (isCorrect) {
      const timeBonus = Math.max(0, (30 - responseTime) * (points * 0.02))
      points += timeBonus

      // Apply streak bonus
      if (gameState.streak > 0) {
        multiplier = Math.min(2, 1 + (gameState.streak * 0.1))
      }
    }

    // Update game state
    setGameState(prev => ({
      ...prev,
      score: prev.score + (isCorrect ? points * multiplier : 0),
      streak: isCorrect ? prev.streak + 1 : 0,
      currentRound: prev.currentRound + 1,
      timeLeft: 30,
      powerUpsActive: []
    }))

    // Check if game is complete
    if (gameState.currentRound >= questions.length - 1) {
      onComplete(gameState.score, responseTime)
      return
    }

    // For best_of_three games, check if we should end early
    if (gameType === 'best_of_three' && isCorrect) {
      const { data: existingScores } = await supabase
        .from('game_scores')
        .select('score')
        .eq('game_id', gameId)
        .eq('user_id', user?.id)
        .order('score', { ascending: false })

      if (existingScores && existingScores.length > 0) {
        const highestScore = existingScores[0].score
        if (gameState.score > highestScore) {
          onComplete(gameState.score, responseTime)
          return
        }
      }
    }
  }

  if (gameState.currentRound >= questions.length) {
    return null
  }

  const question = questions[gameState.currentRound]
  const streakMultiplier = 1 + (gameState.streak * 0.1)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>Question {gameState.currentRound + 1} of {questions.length}</span>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Timer className="h-4 w-4" />
              <span className={gameState.timeLeft <= 5 ? "text-destructive" : ""}>
                {gameState.timeLeft}s
              </span>
            </div>
            <span className="text-lg font-normal">
              Score: {gameState.score}
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-between items-center">
          <PowerUpBar
            powerUps={_powerUps || []}
            onUsePowerUp={_onUsePowerUp || (async () => false)}
            disabled={gameState.powerUpsActive.length > 0}
          />
          <StreakIndicator 
            streak={gameState.streak}
            multiplier={streakMultiplier}
          />
        </div>

        <div className="text-lg font-medium">
          {question.question}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {question.answers.map((answer, index) => (
            <Button
              key={index}
              size="lg"
              className="h-24 text-lg"
              onClick={() => handleAnswer(index)}
            >
              {typeof answer === 'string' ? answer : answer.text}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}